import { HeroBackground, ProductMain, useWindowWidth } from '@prishapolicy/shared/ui';

import {
  ChoosePlan,
  ChoosePlanProps,
  ComparisonTable,
  ComparisonTableProps,
  CostsTicker,
  Faq,
  getPurchaseFlowHeroForms,
  healthfaqList,
  HeroTicker,
  ProductCategoryPageProps,
  productsPathAdder,
  ProtectToday,
  useW3PurchaseFlow,
  WhyChooseUs,
} from '@prishapolicy/w3/ui';
import { NextSeo } from 'next-seo';
import Image from 'next/image';

const productCategorySlug = 'health-and-accidental';

const comparisonTableData: ComparisonTableProps['data'] = {
  heading: 'Difference between Employee Benefit Products vs Individual Products',
  columns: ['Group Insurance', 'Individual Insurance'],
  categoryWise: [
    {
      name: 'Health Insurance',
      rows: [
        {
          name: 'Coverage',
          // tooltipInfo: 'this is a tooltip',
          values: ['Designed as needed', 'Standard'],
        },
        {
          name: 'Premium',
          // tooltipInfo: 'this is a tooltip',
          values: ['Negotiable', 'Standardised'],
        },
        {
          name: 'Age Limit',
          // tooltipInfo: 'this is a tooltip',
          values: ['As per employer choice', 'As per insurance companies guidelines'],
        },
        {
          name: 'Maternity Benefit',
          // tooltipInfo: 'this is a tooltip',
          values: ['Covered from day one', 'Covered with 2-3 years waiting period'],
        },
        {
          name: 'Pre exisiting disease',
          // tooltipInfo: 'this is a tooltip',
          values: ['Can be covered from day 1', 'Covered after 2-4 years waiting period'],
        },
      ],
    },
    {
      name: 'Accidental Insurance',
      rows: [
        {
          name: 'Coverage',
          // tooltipInfo: 'this is a tooltip',
          values: ['Designed as needed', 'Standard'],
        },
        {
          name: 'Premium',
          // tooltipInfo: 'this is a tooltip',
          values: ['Negotiable', 'Standardised'],
        },
        {
          name: 'Family Floater',
          // tooltipInfo: 'this is a tooltip',
          values: ['No', 'Yes'],
        },
        {
          name: 'Medical Extension',
          // tooltipInfo: 'this is a tooltip',
          values: ['Yes', 'No'],
        },
      ],
    },
    {
      name: 'Term Life Insurance',
      rows: [
        {
          name: 'Coverage - suicide',
          values: ['Can be covered from day one', 'After one year'],
        },
        {
          name: 'Premium',
          values: ['Negotiable', 'Standardised'],
        },
        {
          name: 'Minimum Group Size',
          values: ['Required', 'After one year'],
        },
      ],
    },
  ],
};

export function HealthInsurance(props: ProductCategoryPageProps) {
  const choosePlanCards: ChoosePlanProps['cards'] = [
    {
      title: 'Pre & Post Hospitalization ',
      content: (
        <div>
          This coverage helps in the expenses that incurred on an illness or any basic treatment that took place before
          the hospitalization. Post hospitalization cover the expenses after the discharge for a fixed pre-agreed number
          of days.
        </div>
      ),
    },
    {
      title: 'Hospitalization Bills',
      content: (
        <div>
          Expenses incurred on treatment that requires hospitalization for more than 24 hours. It commonly covers rent,
          medicine costs, fees for tests, and doctor’s fees.
        </div>
      ),
    },
    {
      title: 'ICU Charges',
      content: (
        <div>
          Per-day charges for treatment in Intensive Care Units for a pre-authorized duration. All expenses from
          ventilators to certain medications are covered.
        </div>
      ),
    },
    {
      title: 'Pre-existing Diseases',
      content: (
        <div>
          Covers the treatment for majority of the diseases that were already diagnosed after the waiting period of the
          policy it can be anywhere between 2 to 4 years.
        </div>
      ),
    },
    {
      title: 'Medical Check-ups',
      content: (
        <div>
          Provides the insured with free medical check-ups and treatment at predefined intervals according to their
          policy.
        </div>
      ),
    },
    {
      title: 'Ambulance Costs',
      content: (
        <div>Covers the cost of reaching the nearest hospital with all the facilities available in the ambulance.</div>
      ),
    },
  ];
  const { IconRenderer, FormRenderer } = useW3PurchaseFlow(props);
  const { isLg } = useWindowWidth();
  const description = ` Whether you are in the prime of your health, ailed by a chronic condition or suffering from recurring malady, health insurance is the answer to a tension-free life. From medical cost and hospitalization expenses to doctor's consultation fees—a health insurance policy takes care of the heavy expenses and ensures you get the best medical care, without breaking the bank.`;
  return (
    <>
      <NextSeo description={description} title="Health and Accidental Insurance | PrishaPolicy" />
      <ProductMain color="green">
        <HeroBackground backgroundColor="green-300" PurchaseFlowFormRenderer={FormRenderer}>
          <div className="grid-cols-2 lg:grid">
            <div className="flex flex-col justify-between">
              <div>
                <h1 className="heading-1 pb-3">
                  Protect your most
                  <br />
                  precious wealth — <br />
                  Your Health
                </h1>
                <p className="text-title-6 max-w-[600px]">
                  We possess the requisite experience, technical skills and professionalism to provide our customers
                  with robust insurance solutions.
                </p>
              </div>
              <IconRenderer />
            </div>
            {isLg && (
              <div className="grid place-items-center">
                <video src={'/products/health-and-accidental/animation.webm'} autoPlay playsInline muted loop></video>
              </div>
            )}
          </div>
        </HeroBackground>
        <HeroTicker />
        <ProtectToday maxPerRow={5} items={props.productPaths} productCategoryPrefix={productCategorySlug} />
        <div className="container grid-cols-2 gap-x-24 py-24 lg:grid">
          <div className="flex flex-col justify-center">
            <h2 className="lg:heading-3 heading-4">What is Health Insurance?</h2>
            <p className="prose mt-4">{description}</p>
          </div>
          <div className="mt-10 grid place-items-center">
            <Image src={require('./health-stamp.png')} alt="health stamp" />
          </div>
        </div>
        <CostsTicker />
        <ChoosePlan cards={choosePlanCards} />
        <ComparisonTable comparisonTableData={comparisonTableData} />
        <WhyChooseUs />
        <Faq list={healthfaqList} />
      </ProductMain>
    </>
  );
}

export function getStaticProps() {
  // protectCardCheck(productCategorySlug, productsPathAdder(productCategorySlug));
  const productPaths = productsPathAdder(productCategorySlug);
  return {
    props: {
      ...getPurchaseFlowHeroForms(productCategorySlug).props,
      productPaths,
    },
  };
}
export default HealthInsurance;
